/**
 * @file AvitoListingsPage.tsx
 * @description Список Avito-листингов магазина (phase14.0).
 *
 * UX:
 *  - Кнопка «Обновить» — перечитывает кэш из БД.
 *  - Кнопка «Синхронизировать с Avito» — запускает sync с Avito API и обновляет.
 *  - Empty-state с подсказкой запустить синхронизацию.
 *
 * Mobile-first: cards-layout на <md, table на ≥md.
 */

import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  formatPennyToRub,
  getAvitoStatusLabel,
  shopAvito,
  type AvitoListingDto,
} from "../../../lib/api-shop-avito";

export function AvitoListingsPage() {
  const notify = useNotify();
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<AvitoListingDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await shopAvito.listings({ page: 1, perPage: 100 });
      setItems(r.items);
      setTotal(r.meta.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить листинги");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function syncNow(): Promise<void> {
    setSyncing(true);
    try {
      const r = await shopAvito.triggerSync("listings");
      if (r.status === "success") {
        notify.success("Синхронизация листингов завершена");
        await load();
      } else {
        notify.error("Синхронизация завершена с ошибкой — см. журнал");
      }
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось запустить синхру");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Toolbar
        total={total}
        loading={loading}
        syncing={syncing}
        onRefresh={load}
        onSync={syncNow}
      />

      {loading ? (
        <Loader2 className="size-5 animate-spin mx-auto my-16 text-on-surface-variant" />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : items.length === 0 ? (
        <EmptyState onSync={syncNow} syncing={syncing} />
      ) : (
        <>
          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {items.map((it) => (
              <ListingCard key={it.productId} listing={it} slug={slug} />
            ))}
          </div>
          {/* Desktop table */}
          <EditorialCard className="hidden md:block">
            <EditorialCard.Body className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-on-surface-variant border-b border-outline-variant">
                    <tr>
                      <th className="px-4 py-3">Название</th>
                      <th className="px-4 py-3">Avito ID</th>
                      <th className="px-4 py-3">Статус</th>
                      <th className="px-4 py-3 text-right">Цена</th>
                      <th className="px-4 py-3">Адрес</th>
                      <th className="px-4 py-3">Синхра</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const label = getAvitoStatusLabel(it.avitoStatus);
                      return (
                        <tr
                          key={it.productId}
                          className="border-b border-outline-variant last:border-0 hover:bg-surface-low transition-colors"
                        >
                          <td className="px-4 py-3 font-medium">
                            <Link
                              to={`/shop/${slug}/avito/listings/${it.productId}`}
                              className="text-primary hover:underline"
                            >
                              {it.displayName}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant">{it.avitoItemId}</td>
                          <td className="px-4 py-3">
                            <Badge
                              variant={
                                label.tone === "danger"
                                  ? "destructive"
                                  : label.tone === "warn"
                                    ? "warning"
                                    : "default"
                              }
                            >
                              {label.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatPennyToRub(it.pricePenny)}
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant">{it.address ?? "—"}</td>
                          <td className="px-4 py-3 text-on-surface-variant">
                            {new Date(it.lastSyncedAt).toLocaleString("ru-RU")}
                          </td>
                          <td className="px-4 py-3">
                            <a
                              href={it.url}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="text-primary hover:underline inline-flex items-center gap-1"
                            >
                              <ExternalLink className="size-3.5" />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </EditorialCard.Body>
          </EditorialCard>
        </>
      )}
    </div>
  );
}

function Toolbar({
  total,
  loading,
  syncing,
  onRefresh,
  onSync,
}: {
  total: number;
  loading: boolean;
  syncing: boolean;
  onRefresh: () => void;
  onSync: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-on-surface-variant">
        Всего листингов в кэше: <strong className="text-on-surface">{total}</strong>
      </p>
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading || syncing}>
          <RefreshCw className="size-4" />
          Обновить
        </Button>
        <Button onClick={onSync} disabled={syncing} size="sm">
          {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Синхронизировать с Avito
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ onSync, syncing }: { onSync: () => void; syncing: boolean }) {
  return (
    <EditorialCard>
      <EditorialCard.Body className="text-center py-10 space-y-3">
        <p className="text-base font-medium text-on-surface">Листингов нет в кэше</p>
        <p className="text-sm text-on-surface-variant max-w-md mx-auto">
          Запустите синхронизацию, чтобы подтянуть актуальные объявления с Avito API.
        </p>
        <Button onClick={onSync} disabled={syncing}>
          {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Синхронизировать сейчас
        </Button>
      </EditorialCard.Body>
    </EditorialCard>
  );
}

function ListingCard({
  listing,
  slug,
}: {
  listing: AvitoListingDto;
  slug: string | undefined;
}) {
  const label = getAvitoStatusLabel(listing.avitoStatus);
  return (
    <EditorialCard>
      <EditorialCard.Body className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/shop/${slug}/avito/listings/${listing.productId}`}
            className="font-medium text-primary hover:underline"
          >
            {listing.displayName}
          </Link>
          <Badge
            variant={
              label.tone === "danger"
                ? "destructive"
                : label.tone === "warn"
                  ? "warning"
                  : "default"
            }
          >
            {label.label}
          </Badge>
        </div>
        <div className="text-sm text-on-surface-variant flex items-center justify-between">
          <span>{formatPennyToRub(listing.pricePenny)}</span>
          <span>{listing.address ?? "—"}</span>
        </div>
        <div className="text-xs text-on-surface-variant">
          ID {listing.avitoItemId} · синхра {new Date(listing.lastSyncedAt).toLocaleString("ru-RU")}
        </div>
        <a
          href={listing.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-primary text-sm hover:underline inline-flex items-center gap-1"
        >
          Открыть на Avito <ExternalLink className="size-3.5" />
        </a>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
